import { CumulativeBooster } from "../cumulative_booster.js";
import { CumulativeBoosterID } from "../cumulative_booster_id.js";

export interface MultiballTarget
{
  x: number;
  y: number;
  z: number;
}

export interface MultiballBoosterContext
{
  setMultiballActive( active: boolean ): void;
  fireMultiballAt(
    target: MultiballTarget,
    mass_multiplier: number,
    yaw_offset_radians: number
  ): void;
}

export class MultiballBooster extends CumulativeBooster<MultiballBoosterContext>
{
  public static readonly DEFAULT_BALL_COUNT = 10;
  public static readonly DEFAULT_SHOT_INTERVAL_SECONDS = 0.1;
  public static readonly DEFAULT_MASS_MULTIPLIER = 2;
  public static readonly DEFAULT_SPREAD_DEGREES = 5;

  public readonly ballCount: number;
  public readonly shotInterval: number;
  public readonly massMultiplier: number;
  public readonly spreadDegrees: number;

  private balls_remaining = 0;
  private time_to_next_ball = 0;
  private target: MultiballTarget | null = null;

  public constructor(
    ball_count = MultiballBooster.DEFAULT_BALL_COUNT,
    shot_interval = MultiballBooster.DEFAULT_SHOT_INTERVAL_SECONDS,
    mass_multiplier = MultiballBooster.DEFAULT_MASS_MULTIPLIER,
    spread_degrees = MultiballBooster.DEFAULT_SPREAD_DEGREES
  )
  {
    super( CumulativeBoosterID.Multiball );

    if ( !Number.isFinite( ball_count ) || ball_count <= 0 )
      throw new Error( "Multiball count must be a finite positive number" );

    if ( !Number.isFinite( shot_interval ) || shot_interval < 0 )
      throw new Error( "Multiball shot interval must be a finite non-negative number" );

    if ( !Number.isFinite( mass_multiplier ) || mass_multiplier <= 0 )
      throw new Error( "Multiball mass multiplier must be a finite positive number" );

    if ( !Number.isFinite( spread_degrees ) || spread_degrees < 0 )
      throw new Error( "Multiball spread must be a finite non-negative number" );

    this.ballCount = Math.floor( ball_count );
    this.shotInterval = shot_interval;
    this.massMultiplier = mass_multiplier;
    this.spreadDegrees = spread_degrees;
  }

  /** Called after the player's first ball has actually been created. */
  public onPrimaryBallFired( target: MultiballTarget ): void
  {
    if ( !this.isActive || this.target !== null )
      return;

    this.target = { x: target.x, y: target.y, z: target.z };
    this.balls_remaining = this.ballCount - 1;
    this.time_to_next_ball = this.shotInterval;

    if ( this.balls_remaining === 0 )
      this.complete();
  }

  protected override onActivate( context: MultiballBoosterContext ): void
  {
    context.setMultiballActive( true );
  }

  protected override onUpdate( context: MultiballBoosterContext, dt: number ): void
  {
    if ( this.target === null )
      return;

    this.time_to_next_ball -= dt;

    while ( this.balls_remaining > 0 && this.time_to_next_ball <= 0 )
    {
      const yaw_offset_radians = (
        ( Math.random() * 2 - 1 ) * this.spreadDegrees * Math.PI / 180
      );
      context.fireMultiballAt(
        this.target,
        this.massMultiplier,
        yaw_offset_radians
      );
      this.balls_remaining--;
      this.time_to_next_ball += this.shotInterval;

      // With a zero interval every remaining ball belongs to this update.
      if ( this.shotInterval === 0 )
        continue;
    }

    if ( this.balls_remaining === 0 )
      this.complete();
  }

  protected override onDeactivate( context: MultiballBoosterContext, _cancelled: boolean ): void
  {
    context.setMultiballActive( false );
  }

  protected override onReset(): void
  {
    this.balls_remaining = 0;
    this.time_to_next_ball = 0;
    this.target = null;
  }
}
